import { useEffect, useRef } from 'react';

interface SegmentConfig {
    segment: string;
    label?: string;
    onMatch?: (params: { lastSegment: string; searchParams: URLSearchParams }) => void;
}

interface UseBreadcrumbUpdaterOptions {
    segments: SegmentConfig[];
    headingSelector?: string;
    breadcrumbSelector?: string;
    checkReferrer?: boolean;
}

export const useBreadcrumbUpdater = ({
    segments,
    headingSelector = '.container-heading',
    breadcrumbSelector = '.ms-breadcrumb_link',
    checkReferrer = false
}: UseBreadcrumbUpdaterOptions) => {
    const lastProcessedUrl = useRef<string | null>(null);

    useEffect(() => {
        if (!window.location || !window.location.href) {
            return;
        }

        try {
            const url = new URL(window.location.href);
            const currentUrl = url.toString();

            let referrerUrl: URL | undefined, lastReferrerSegment: string | undefined;
            if (checkReferrer && window.document.referrer) {
                try {
                    referrerUrl = new URL(window.document.referrer);
                    lastReferrerSegment = referrerUrl.pathname
                        .split('/')
                        .filter(Boolean)
                        .pop();
                } catch (referrerError) {
                    console.error('Invalid referrer URL:', referrerError);
                    return;
                }
            }

            if (lastProcessedUrl.current === currentUrl) {
                return;
            }

            const lastSegment: string =
                url.pathname
                    .split('/')
                    .filter(Boolean)
                    .pop() || '';

            const segmentToMatch: string = checkReferrer ? lastReferrerSegment || '' : lastSegment;
            const searchParams = url.searchParams;
            const match = segments.find(s => s.segment === segmentToMatch);

            if (!match) {
                return;
            }

            const labelToUse = match.label;

            if (headingSelector && labelToUse && !checkReferrer) {
                const heading = document.querySelector(headingSelector);
                if (heading) {
                    heading.innerHTML = labelToUse;
                }
            }

            const breadcrumbs = document.querySelectorAll(breadcrumbSelector);
            if (breadcrumbs.length > 1) {
                const breadcrumbToUpdate = checkReferrer ? breadcrumbs[1] : breadcrumbs[breadcrumbs.length - 1];

                if (checkReferrer && referrerUrl) {
                    breadcrumbToUpdate.setAttribute('href', referrerUrl.pathname);
                } else if (labelToUse) {
                    breadcrumbToUpdate.innerHTML = labelToUse;
                }
            }

            if (match.onMatch) {
                match.onMatch({ lastSegment: segmentToMatch, searchParams });
            }

            lastProcessedUrl.current = currentUrl;
        } catch (error) {
            console.error('Error processing URL:', error);
        }
    }, [segments, headingSelector, breadcrumbSelector, checkReferrer]);
};
